import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, X, Plus, Loader2, GripVertical, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth, API } from '../App';

// Helper to get full photo URL
const getPhotoUrl = (photo) => {
  if (!photo) return null;
  if (photo.startsWith('http')) return photo;
  return `${API.replace('/api', '')}${photo}`;
};

export default function PostDealPage() {
  const navigate = useNavigate();
  const { dealId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingDeal, setLoadingDeal] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [photos, setPhotos] = useState([]); // New photos to upload {file, preview}
  const [existingPhotos, setExistingPhotos] = useState([]); // Already uploaded photos (URLs)
  const [photosToDelete, setPhotosToDelete] = useState([]); // Photos marked for deletion
  const [draggedIndex, setDraggedIndex] = useState(null);
  const isEditMode = !!dealId;
  
  const [formData, setFormData] = useState({
    title: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    property_type: '',
    asking_price: '',
    arv: '',
    rehab_cost: '',
    bedrooms: '',
    bathrooms: '',
    sqft: '',
    lot_size: '',
    occupancy_status: '',
    description: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    rental_estimate: '',
    estimated_roi: '',
    deal_type: 'wholesale'
  });

  const propertyTypes = ['Single Family', 'Multi-Family', 'Townhouse', 'Condo', 'Duplex', 'Triplex', 'Fourplex'];
  const dealTypes = [
    { value: 'wholesale', label: 'Wholesale' },
    { value: 'fix_and_flip', label: 'Fix & Flip' },
    { value: 'buy_and_hold', label: 'Buy & Hold' },
    { value: 'turnkey', label: 'Turnkey' },
    { value: 'foreclosure', label: 'Foreclosure' },
    { value: 'short_sale', label: 'Short Sale' }
  ];
  const occupancyStatuses = ['Vacant', 'Owner Occupied', 'Tenant Occupied', 'Month-to-Month'];
  const states = ['TX', 'AZ', 'GA', 'FL', 'TN', 'NC', 'CO', 'NV', 'IN', 'OH', 'CA', 'NY', 'PA', 'IL', 'MI'];

  // Load existing deal data when editing
  useEffect(() => {
    if (isEditMode && dealId) {
      loadDealData();
    }
  }, [dealId]);

  const loadDealData = async () => {
    setLoadingDeal(true);
    try {
      const response = await axios.get(`${API}/deals/${dealId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const deal = response.data;
      
      setFormData({
        title: deal.title || '',
        address: deal.address || '',
        city: deal.city || '',
        state: deal.state || '',
        zip_code: deal.zip_code || '',
        property_type: deal.property_type || '',
        asking_price: deal.asking_price?.toString() || '',
        arv: deal.arv?.toString() || '',
        rehab_cost: deal.rehab_cost?.toString() || '',
        bedrooms: deal.bedrooms?.toString() || '',
        bathrooms: deal.bathrooms?.toString() || '',
        sqft: deal.sqft?.toString() || '',
        lot_size: deal.lot_size?.toString() || '',
        occupancy_status: deal.occupancy_status || '',
        description: deal.description || '',
        contact_name: deal.contact_name || '',
        contact_email: deal.contact_email || '',
        contact_phone: deal.contact_phone || '',
        rental_estimate: deal.rental_estimate?.toString() || '',
        estimated_roi: deal.estimated_roi?.toString() || '',
        deal_type: deal.deal_type || 'wholesale'
      });
      
      // Load existing photos
      if (deal.photos && deal.photos.length > 0) {
        setExistingPhotos(deal.photos);
      }
    } catch (error) {
      toast.error('Failed to load deal data');
      navigate('/dashboard');
    } finally {
      setLoadingDeal(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const totalPhotos = photos.length + existingPhotos.length - photosToDelete.length;
    if (totalPhotos + files.length > 10) {
      toast.error('Maximum 10 photos allowed');
      return;
    }

    files.forEach(file => {
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Max 5MB per image.`);
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, { file, preview: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeNewPhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingPhoto = (photoUrl) => {
    setPhotosToDelete(prev => [...prev, photoUrl]);
  };

  const restorePhoto = (photoUrl) => {
    setPhotosToDelete(prev => prev.filter(p => p !== photoUrl));
  };

  // Drag and drop for reordering existing photos
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    
    const newPhotos = [...existingPhotos];
    const draggedPhoto = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(index, 0, draggedPhoto);
    setExistingPhotos(newPhotos);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation - only essential fields required
    const required = ['title', 'address', 'city', 'state', 'zip_code', 'property_type', 
                     'deal_type', 'asking_price', 'contact_name', 'contact_email'];
    
    for (const field of required) {
      if (!formData[field]) {
        toast.error(`Please fill in ${field.replace(/_/g, ' ')}`);
        return;
      }
    }

    setLoading(true);

    try {
      const dealPayload = {
        ...formData,
        asking_price: parseFloat(formData.asking_price),
        arv: formData.arv ? parseFloat(formData.arv) : null,
        rehab_cost: formData.rehab_cost ? parseFloat(formData.rehab_cost) : null,
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseFloat(formData.bathrooms) : null,
        sqft: formData.sqft ? parseInt(formData.sqft) : null,
        rental_estimate: formData.rental_estimate ? parseFloat(formData.rental_estimate) : null,
        estimated_roi: formData.estimated_roi ? parseFloat(formData.estimated_roi) : null
      };

      let targetDealId = dealId;

      if (isEditMode) {
        // Update existing deal
        await axios.put(`${API}/deals/${dealId}`, dealPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        // Update photos array (reordered, minus deleted)
        const remainingPhotos = existingPhotos.filter(p => !photosToDelete.includes(p));
        await axios.put(`${API}/deals/${dealId}/photos`, 
          { photos: remainingPhotos },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new deal
        const response = await axios.post(`${API}/deals`, dealPayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        targetDealId = response.data.deal_id;
      }

      // Upload new photos
      if (photos.length > 0) {
        setUploadingPhotos(true);
        let uploadedCount = 0;
        for (const photo of photos) {
          const photoFormData = new FormData();
          photoFormData.append('file', photo.file);
          
          try {
            await axios.post(`${API}/deals/${targetDealId}/photos`, photoFormData, {
              headers: { Authorization: `Bearer ${token}` }
            });
            uploadedCount++;
          } catch (err) {
            console.error('Photo upload error:', err);
            toast.error(`Failed to upload ${photo.file?.name || 'photo'}`);
          }
        }
        if (uploadedCount > 0) {
          toast.success(`${uploadedCount} photo${uploadedCount > 1 ? 's' : ''} uploaded!`);
        }
      }

      toast.success(isEditMode ? 'Deal updated successfully!' : 'Deal posted successfully!');
      navigate(`/deals/${targetDealId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'post'} deal`);
    } finally {
      setLoading(false);
      setUploadingPhotos(false);
    }
  };

  if (loadingDeal) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-[#5DADE2]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-6 py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </button>

        <div className="bg-white rounded-xl border border-slate-200 p-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="post-deal-title">
            {isEditMode ? 'Edit Deal' : 'Post a Deal'}
          </h1>
          <p className="text-slate-600 mb-8">
            {isEditMode ? 'Update your property listing' : 'List your investment property for potential buyers'}
          </p>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Property Info */}
            <div className="space-y-6">
              <h2 className="font-semibold text-lg text-slate-900 border-b border-slate-200 pb-2">
                Property Information
              </h2>
              
              <div>
                <Label htmlFor="title">Property Title</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., 3BR/2BA Ranch in Austin"
                  className="mt-1"
                  data-testid="deal-title-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="address">Street Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main St"
                    className="mt-1"
                    data-testid="deal-address-input"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Austin"
                    className="mt-1"
                    data-testid="deal-city-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>State</Label>
                  <Select value={formData.state} onValueChange={(v) => handleSelectChange('state', v)}>
                    <SelectTrigger className="mt-1" data-testid="deal-state-select">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="zip_code">ZIP Code</Label>
                  <Input
                    id="zip_code"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={handleChange}
                    placeholder="78701"
                    className="mt-1"
                    data-testid="deal-zip-input"
                  />
                </div>
                <div>
                  <Label>Property Type</Label>
                  <Select value={formData.property_type} onValueChange={(v) => handleSelectChange('property_type', v)}>
                    <SelectTrigger className="mt-1" data-testid="deal-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyTypes.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="bedrooms">Bedrooms</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-beds-input"
                  />
                </div>
                <div>
                  <Label htmlFor="bathrooms">Bathrooms</Label>
                  <Input
                    id="bathrooms"
                    name="bathrooms"
                    type="number"
                    step="0.5"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-baths-input"
                  />
                </div>
                <div>
                  <Label htmlFor="sqft">Square Feet</Label>
                  <Input
                    id="sqft"
                    name="sqft"
                    type="number"
                    value={formData.sqft}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-sqft-input"
                  />
                </div>
                <div>
                  <Label htmlFor="lot_size">Lot Size</Label>
                  <Input
                    id="lot_size"
                    name="lot_size"
                    value={formData.lot_size}
                    onChange={handleChange}
                    placeholder="0.25 acres"
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Occupancy Status</Label>
                  <Select value={formData.occupancy_status} onValueChange={(v) => handleSelectChange('occupancy_status', v)}>
                    <SelectTrigger className="mt-1" data-testid="deal-occupancy-select">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {occupancyStatuses.map(status => (
                        <SelectItem key={status} value={status}>{status}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Deal Type</Label>
                  <Select value={formData.deal_type} onValueChange={(v) => handleSelectChange('deal_type', v)}>
                    <SelectTrigger className="mt-1" data-testid="deal-dealtype-select">
                      <SelectValue placeholder="Select deal type" />
                    </SelectTrigger>
                    <SelectContent>
                      {dealTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Financial Info */}
            <div className="space-y-6">
              <h2 className="font-semibold text-lg text-slate-900 border-b border-slate-200 pb-2">
                Deal Numbers
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="asking_price">Asking Price ($)</Label>
                  <Input
                    id="asking_price"
                    name="asking_price"
                    type="number"
                    value={formData.asking_price}
                    onChange={handleChange}
                    placeholder="185000"
                    className="mt-1"
                    data-testid="deal-price-input"
                  />
                </div>
                <div>
                  <Label htmlFor="arv">ARV ($)</Label>
                  <Input
                    id="arv"
                    name="arv"
                    type="number"
                    value={formData.arv}
                    onChange={handleChange}
                    placeholder="275000"
                    className="mt-1"
                    data-testid="deal-arv-input"
                  />
                </div>
                <div>
                  <Label htmlFor="rehab_cost">Est. Rehab Cost ($)</Label>
                  <Input
                    id="rehab_cost"
                    name="rehab_cost"
                    type="number"
                    value={formData.rehab_cost}
                    onChange={handleChange}
                    placeholder="35000"
                    className="mt-1"
                    data-testid="deal-rehab-input"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="rental_estimate">Rental Estimate ($/mo) - Optional</Label>
                  <Input
                    id="rental_estimate"
                    name="rental_estimate"
                    type="number"
                    value={formData.rental_estimate}
                    onChange={handleChange}
                    placeholder="1800"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="estimated_roi">Est. ROI (%) - Optional</Label>
                  <Input
                    id="estimated_roi"
                    name="estimated_roi"
                    type="number"
                    step="0.1"
                    value={formData.estimated_roi}
                    onChange={handleChange}
                    placeholder="25.5"
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="space-y-4">
              <h2 className="font-semibold text-lg text-slate-900 border-b border-slate-200 pb-2">
                Description
              </h2>
              <div>
                <Label htmlFor="description">Deal Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  placeholder="Describe the property, its condition, and why it's a good investment opportunity..."
                  className="mt-1"
                  data-testid="deal-description-input"
                />
              </div>
            </div>

            {/* Photos */}
            <div className="space-y-4">
              <h2 className="font-semibold text-lg text-slate-900 border-b border-slate-200 pb-2">
                Photos {isEditMode && existingPhotos.length > 0 && <span className="text-sm font-normal text-slate-500">(drag to reorder)</span>}
              </h2>
              
              {/* Existing Photos (Edit Mode) */}
              {isEditMode && existingPhotos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-3">Current Photos</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {existingPhotos.map((photoUrl, index) => {
                      const isDeleted = photosToDelete.includes(photoUrl);
                      return (
                        <div 
                          key={photoUrl}
                          draggable={!isDeleted}
                          onDragStart={() => handleDragStart(index)}
                          onDragOver={(e) => handleDragOver(e, index)}
                          onDragEnd={handleDragEnd}
                          className={`relative aspect-square rounded-lg overflow-hidden bg-slate-100 ${
                            isDeleted ? 'opacity-40' : 'cursor-grab active:cursor-grabbing'
                          } ${draggedIndex === index ? 'ring-2 ring-blue-500' : ''}`}
                        >
                          <img 
                            src={getPhotoUrl(photoUrl)} 
                            alt={`Photo ${index + 1}`} 
                            className="w-full h-full object-cover"
                          />
                          {!isDeleted && (
                            <div className="absolute top-2 left-2 p-1 bg-white/80 rounded-md">
                              <GripVertical className="h-4 w-4 text-slate-500" />
                            </div>
                          )}
                          {isDeleted ? (
                            <button
                              type="button"
                              onClick={() => restorePhoto(photoUrl)}
                              className="absolute inset-0 flex items-center justify-center bg-slate-900/50"
                            >
                              <span className="px-3 py-1 bg-white text-slate-700 rounded-full text-sm font-medium">
                                Restore
                              </span>
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => removeExistingPhoto(photoUrl)}
                              className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                          {index === 0 && !isDeleted && (
                            <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                              Cover
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* New Photos */}
              {photos.length > 0 && (
                <div className="mb-4">
                  <p className="text-sm text-slate-600 mb-3">New Photos to Upload</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {photos.map((photo, index) => (
                      <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 ring-2 ring-green-500">
                        <img src={photo.preview} alt={`New ${index + 1}`} className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removeNewPhoto(index)}
                          className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <span className="absolute bottom-2 left-2 px-2 py-0.5 bg-green-600 text-white text-xs rounded-full">
                          New
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Upload Button */}
              {(photos.length + existingPhotos.length - photosToDelete.length) < 10 && (
                <label className="inline-flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors">
                  <Upload className="h-5 w-5 text-slate-400" />
                  <span className="text-sm text-slate-600">Add Photos</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              )}
              <p className="text-sm text-slate-500">Upload up to 10 photos (max 5MB each). First photo will be the cover image.</p>
            </div>

            {/* Contact Info */}
            <div className="space-y-6">
              <h2 className="font-semibold text-lg text-slate-900 border-b border-slate-200 pb-2">
                Contact Information
              </h2>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="contact_name">Contact Name</Label>
                  <Input
                    id="contact_name"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-contact-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-contact-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact_phone">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    name="contact_phone"
                    value={formData.contact_phone}
                    onChange={handleChange}
                    className="mt-1"
                    data-testid="deal-contact-phone-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-6 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || uploadingPhotos}
                className="flex-1 bg-blue-700 hover:bg-blue-800 rounded-full"
                data-testid="submit-deal-btn"
              >
                {loading || uploadingPhotos ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {uploadingPhotos ? 'Uploading Photos...' : (isEditMode ? 'Saving...' : 'Posting...')}
                  </>
                ) : (
                  <>
                    {isEditMode ? null : <Plus className="h-4 w-4 mr-2" />}
                    {isEditMode ? 'Save Changes' : 'Post Deal'}
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
